import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Grid,
  FormControl,
  MenuItem,
  Select,
  TextField,
  FormHelperText,
} from '@mui/material';
import {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormGetValues,
  useFieldArray,
  Controller,
} from 'react-hook-form';
import RenderProductInfo from './RenderProductInfo';
import {
  PRODUCT_TYPES,
  RENTAL_SERVICE_TYPE,
} from '../../../Utils/constantData';
import CustomButton from '../../../Components/CustomButton'; // Adjust path accordingly
import AutocompleteSelect from '../../../Components/AutocompleteSelect';
import DateInput from '../../../Components/DateInput';

import {
  autoEndTime,
  coverageDetails,
  generateTimeStamp,
  generateTimeStampOffset,
} from '../../../Utils/helperFns';

import Images from '../../../Utils/images';
import SelectAddressDialog from './SelectAddressDialog';
import {
  checkDeliverability,
  getBusinessAddress,
  getSchdules,
  orderLocationMethod,
} from '../../../redux/riskCenterSlice';
import { getProductionListingforDropdown } from '../../../redux/dashboardSlice';
import {
  clearFields,
  getDepartmentList,
  getShiftQuotation,
  setQuantity,
  setShifts,
  setShiftsError,
} from '../../../redux/orderSlice';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getUserProfileData } from '../../../redux/commonSlice';
import { RootState, useAppSelector } from '../../../redux/store';

import Loading from '../../../Components/Loading';
import { getProductionDetails } from '../../../redux/ProductionSlice';
import TimePicker from '../../../Components/TimePicker';
import { TIME_PICKER_MINUTES_STEP } from '../../../helpers/contants';
import dayjs from 'dayjs';
import { ImageCropperInput } from './ImageCropperInput';
import WarningDialog from '../../../Components/WarningDialog';
import MapModal from './MapModal';
import PreviewDialog from './PreviewDialog';

interface OrderFormProps {
  control: Control<any>;
  errors: FieldErrors<any>;
  setValue: UseFormSetValue<any>;
  handleSubmit: UseFormHandleSubmit<any>;
  register: UseFormRegister<any>;
  trigger: UseFormTrigger<any>;
  getValues: UseFormGetValues<any>;
  watch: (name: string) => any;
  rentIndex: number | string;
  type: string;
  isValid: boolean;
  onSubmit: (data: any, submitType: string) => void;
  data: any;
  setError: any;
  reset: any;
  apiLoading?: boolean;
  setHasChangedAnyValue?: any;
}

const OrderForm: React.FC<OrderFormProps> = ({
  control,
  errors,
  setValue,
  apiLoading,
  handleSubmit,
  register,
  reset,
  trigger,
  getValues,
  watch,
  rentIndex,
  type,
  isValid,
  data,
  setError,
  onSubmit,
  setHasChangedAnyValue,
}) => {
  const location = watch('Location');
  const dispatch = useDispatch();
  const [load, setLoad] = useState(false);

  const { productionList } = useAppSelector(state => state.dashboard);
  const { quantity, shifts, departmentList, shiftsError } = useAppSelector(
    state => state.order,
  );

  const preview = useAppSelector(state => state.common.previewShiftDetails);
  const id = useMemo(() => {
    return data?._id;
  }, [data]);
  const { cartDetails } = useAppSelector(state => state.cart);
  const { startOrderData } = useAppSelector(state => state.common);
  const [warningOpen, setWarningOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [openSelectAddress, setOpenSelectAddress] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(0);
  const { selectedAddress } = useAppSelector(state => state.common);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [zoneData, setZoneData] = useState<{
    center: { latitude: number; longitude: number };
    radius: number;
    label: string;
  }>({
    center: { latitude: 0, longitude: 0 },
    radius: 0,
    label: '',
  });
  const [loadingState, setLoading] = useState(false);
  const { deliveryCheck, schdulesTypes } = useAppSelector(
    (state: RootState) => state.riskCenter,
  );
  const rentPriceId = useMemo(() => {
    if (type?.includes('RENT')) {
      return data?.rentPricing?.[rentIndex]?._id;
    }
  }, [data, rentIndex, type]);
  const { loading } = useAppSelector(state => state.store);

  const lastProductionRef = useRef<any>(null);
  // const lastLocationRef = useRef<any>(null);
  const { branchId } = data;
  const [previewDoc, setPreviewDoc] = useState<null | {
    url: string;
    name: string;
  }>(null);
  const { state } = useLocation();
  const handleClose = () => {
    setPreviewDoc(null);
  };
  const {
    fields: documentFields,
    append: appendDocuments,
    remove: removeDocuments,
  } = useFieldArray({
    control,
    name: 'documents',
  });

  // getLastShift in case of hourly model of service
  const getShift = () => {
    const endTime = getValues('End Time');
    const startTime = getValues('Start Time');
    const endDate = getValues('End Date');
    const startDate = getValues('Start Date');
    const latitude = getValues('Location')?.location?.coordinates?.[1];
    const longitude = getValues('Location')?.location?.coordinates?.[0];

    dispatch(setShiftsError(''));
    dispatch(setShifts(''));
    if (!startTime || !endTime || !startDate || !endDate) return;
    if (
      latitude == null ||
      latitude === '' ||
      longitude == null ||
      longitude === ''
    )
      return;
    if (
      startDate &&
      startTime &&
      endDate &&
      endTime &&
      (`${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` == type ||
        `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` == type) &&
      data?.rentPricing?.[rentIndex]?.priceMode ==
        `${RENTAL_SERVICE_TYPE.PER_HOUR}` &&
      isValid
    ) {
      const payload = {
        qty: quantity,
        productId: data._id,
        latitude,
        longitude,
        isPackage: type?.startsWith(`${PRODUCT_TYPES.PACKAGE}`),
        addressType: 'BUSINESS',
        productionId: getValues('Production')?._id,
        rentPriceId: data?.rentPricing?.[rentIndex]?._id,
        startTime: generateTimeStamp(startDate, startTime),
        endTime: generateTimeStamp(endDate, endTime),
        shiftCoverage: data?.rentPricing?.[rentIndex]?.isFlexibleShiftHr
          ? 'Flexible'
          : getValues('Coverage') == 'autoCoverage'
          ? coverageDetails(
              getValues('Start Time'),
              data.rentPricing?.[rentIndex]?.shiftLength,
            )?.includes('Day')
            ? 'Day'
            : 'Night'
          : getValues('Coverage')?.includes('Night')
          ? 'Night'
          : getValues('Coverage') == '24 Hrs Shift'
          ? '24 Hrs'
          : getValues('Coverage') == 'Specific Duration'
          ? 'Swing Shift'
          : getValues('Coverage'),
        startOffset: generateTimeStampOffset(startDate, startTime),
        endOffset: generateTimeStampOffset(endDate, endTime),
      };

      getShiftQuotation(payload);
    }
  };

  // autoPopulate end time
  const populateEndTime = () => {
    if (
      getValues('Start Time') &&
      getValues('Coverage') &&
      getValues('Start Date') &&
      getValues('End Date') &&
      (type == `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ||
        type == `${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}`)
    ) {
      const startTime = getValues('Start Time');
      let computedEndTime: dayjs.Dayjs | null = null;

      computedEndTime = autoEndTime(
        startTime,
        coverageDetails(
          getValues('Start Time'),
          data.rentPricing?.[rentIndex]?.shiftLength,
        ) ?? '12hrs',
        data.rentPricing?.[rentIndex]?.shiftLength,
      );

      if (computedEndTime) {
        setValue('End Time', computedEndTime, {
          shouldValidate: true,
        });
        trigger('End Time');
      }
    }
  };

  // changing location on click
  const handleLocation = (data: any) => {
    orderLocationMethod(data);
    checkDeliverability({
      latitude: data?.location?.coordinates?.[1],
      longitude: data?.location?.coordinates?.[0],
      branchId: branchId,
    });
    // lastLocationRef.current = data;
    setValue('Location', data, { shouldValidate: true });
    setValue(
      'Production',
      { name: data.productionName, _id: data.productionId },
      { shouldValidate: true },
    );
    getDepartmentList(data.productionId, (data: any) => {
      setValue('Department', data[0], { shouldValidate: true });
    });
    let params = `?productionId=${data.productionId}&setId=${data.setId}&itemId=${id}`;
    if (rentPriceId) {
      params = `?productionId=${data.productionId}&setId=${data.setId}&itemId=${id}&rentPriceId=${rentPriceId}`;
    }
    getSchdules(params, (schdulesTypes: any) => {
      setValue(
        'Production Schedule',
        schdulesTypes?.productionScheduleType?.[0],
        { shouldValidate: true },
      );
      setValue('Set Schedule', schdulesTypes?.setScheduleType?.[0], {
        shouldValidate: true,
      });
    });
    setOpenSelectAddress(false);
  };

  useEffect(() => {
    getShift();
  }, [
    getValues('End Date'),
    getValues('End Time'),
    getValues('Start Date'),
    getValues('Start Time'),
    watch('Coverage'),
    isValid,
  ]);
  // autofilling data based on various conditions
  useEffect(() => {
    const init = async () => {
      if (Object.keys(startOrderData || {}).length > 0 && id) {
        reset(
          {
            Production: startOrderData.production,
            Location: startOrderData.location,
            'Production Schedule': startOrderData.productionScheduleType,
            'Set Schedule': startOrderData.setScheduleType,
            Department: startOrderData.departMent,
            'Start Date': preview.shiftTimes?.fromDate
              ? dayjs(preview.shiftTimes?.fromDate).format('MMM DD, YYYY')
              : dayjs(startOrderData.startDate),
            'Start Time': preview.shiftTimes?.fromDate
              ? dayjs(preview.shiftTimes?.fromDate)
              : dayjs(startOrderData.startTime),
            'End Date': preview.shiftTimes?.toDate
              ? dayjs(preview.shiftTimes?.toDate).format('MMM DD, YYYY')
              : dayjs(startOrderData.endDate),
            'End Time': preview.shiftTimes?.toDate
              ? dayjs(preview.shiftTimes?.toDate)
              : dayjs(startOrderData.endTime),
            Coverage: startOrderData.shiftCoverage,
            Attachments: startOrderData.attachments,
            Notes: startOrderData.notes,
          },
          { keepErrors: true },
        );
        getShift();
        // setShifts(startOrderData.shifts);
        setQuantity(startOrderData.quantity);
        setLoading(false);
        getProductionListingforDropdown();
        getDepartmentList(startOrderData.location.productionId);
        let params = `?productionId=${startOrderData.location.productionId}&setId=${startOrderData.location.setId}`;
        if (rentPriceId) {
          params = params + `&rentPriceId=${rentPriceId}`;
        }
        if (id) {
          params = params + `&itemId=${id}`;
        }
        getSchdules(params);
        await trigger();
        return;
      } else {
        setLoading(true);
        getProductionListingforDropdown();
        if (state?.production) {
          getBusinessAddress('', () => {
            console.log('state.production', state.production);
            setValue(
              'Production',
              {
                name: state.production.label,
                _id: state.production.id,
                type: state.production.type,
              },
              { shouldValidate: true },
            );
          });

          setLoading(false);
        } else if (selectedAddress.productionId && id) {
          setValue('Production', {
            name: selectedAddress.productionName,
            _id: selectedAddress.productionId,
            type: selectedAddress.productionType,
          });
          checkDeliverability({
            latitude: selectedAddress.location.coordinates[1],
            longitude: selectedAddress.location.coordinates[0],
            branchId: branchId,
          });
          setValue('Location', selectedAddress);
          getDepartmentList(selectedAddress.productionId, (data: any) => {
            setValue('Department', data[0], { shouldValidate: true });
          });

          let params = `?productionId=${selectedAddress.productionId}&setId=${selectedAddress.setId}`;
          if (rentPriceId) {
            params = params + `&rentPriceId=${rentPriceId}`;
          }
          if (id) {
            params = params + `&itemId=${id}`;
          }
          getSchdules(
            params,
            (schdulesTypes: any) => {
              setLoading(false);

              setValue(
                'Production Schedule',
                schdulesTypes?.productionScheduleType?.[0],
                { shouldValidate: true },
              );
              setValue('Set Schedule', schdulesTypes?.setScheduleType?.[0], {
                shouldValidate: true,
              });
            },
            () => setLoading(false),
          );
          // lastLocationRef.current = selectedAddress;
        } else if (!selectedAddress.productionId && id) {
          setLoading(true);
          getUserProfileData(
            (data: any) => {
              if (
                data.defaultEntity.entityType == 'productions' &&
                !selectedAddress.productionId
              ) {
                getProductionDetails(
                  data.defaultEntity.entityId,
                  (production: any) => {
                    setValue('Production', {
                      name: production?.name,
                      _id: production?._id,
                      type: production?.type,
                    });
                    lastProductionRef.current = production;
                  },
                );
                setValue('Department', null);
                setValue('Production Schedule', null);
                setValue('Set Schedule', null);
                setValue('Location', null);
              } else {
                setValue('Production', null);
                setValue('Location', null);
                setValue('Production Schedule', null);
                setValue('Set Schedule', null);
                setValue('Location', null);

                // lastLocationRef.current = null;
                lastProductionRef.current = null;
              }

              setLoading(false);
            },
            () => setLoading(false),
          );
        }
      }
    };
    setHasChangedAnyValue(false);
    init();
  }, [id]);

  // priceMode setting for different validations for different products
  useEffect(() => {
    if (data && data.rentPricing && data.rentPricing.length > 0) {
      setValue('priceMode', data?.rentPricing[rentIndex]);
    }
  }, [data, rentIndex]);
  const clearEntity = () => {
    const fieldsToClear = ['Department', 'Production Schedule', 'Set Schedule'];
    clearFields();
    fieldsToClear.forEach(field => {
      setValue(field, null, { shouldDirty: true });
    });
  };
  const handleAddDocument = (docDetails: {
    deptId: string;
    docName: string;
    type: string;
    url: string;
    size: number;
  }) => {
    if (documentFields.length < 5) {
      appendDocuments(docDetails);
    } // Add the new document to the documents array
    else {
      setError('Attachments', {
        type: 'manual',
        message: 'You can only add up to 5 documents',
      });
    }
  };

  useEffect(() => {
    setValue('Attachments', documentFields, { shouldValidate: true });
  }, [documentFields]);

  if (loading) return <Loading type="div" height="full" />;

  return (
    <>
      <form className="no-shodow-input pb-20">
        <Grid container spacing={2} className="order-wrapper">
          <Grid item lg={9} md={8} xs={12} className="order-main">
            <Grid container spacing={2}>
              <Grid item lg={6} xs={12}>
                <div className="form-in lg left-icon bg-white mb-16">
                  <FormControl fullWidth>
                    <div className="f-in">
                      <AutocompleteSelect
                        id="Production"
                        name={`Production`}
                        control={control}
                        // labelText="Select Production"
                        labelKey="name"
                        placeHolderText="Select Production"
                        options={productionList.length ? productionList : []}
                        error={!!errors['Production'] && !!errors['Production']}
                        setValue={setValue}
                        helperText={
                          !!errors['Production']
                            ? (errors['Production']?.message as string)
                            : ''
                        }
                        onChange={(_: any, newValue: any) => {
                          setValue('Production', newValue);
                          if (lastProductionRef.current?._id == newValue?._id)
                            return;
                          setHasChangedAnyValue(true);
                          setValue('Location', null);
                          clearEntity();
                          clearFields();
                          lastProductionRef.current = newValue;
                          // lastLocationRef.current = null;
                        }}
                        showFrontIcon={
                          <span className="icon-input">
                            <i className="production-icon"></i>
                          </span>
                        }
                        requiredField
                      />
                    </div>
                  </FormControl>
                </div>
              </Grid>
              <Grid item lg={6} xs={12}>
                <div
                  className="form-in lg left-icon mb-16"
                  onClick={() => {
                    if (getValues('Production')) setOpenSelectAddress(true);
                  }}
                >
                  <FormControl fullWidth>
                    <div className="f-in">
                      <div className="address-card lg mt0 neutral-icon">
                        <span className="icon">
                          <i className="location-icon"></i>
                        </span>
                        <div className="text">
                          <p className=" text-text">
                            {location?.locationName
                              ? location.setName?.toUpperCase() +
                                ' | ' +
                                location?.locationName
                              : 'Select Address'}
                          </p>
                        </div>
                        <button type="button" className="blank-btn ml-auto">
                          <i className="edit-icon"></i>
                        </button>
                      </div>
                    </div>
                  </FormControl>
                </div>
              </Grid>
            </Grid>
            {deliveryCheck &&
              !deliveryCheck.isDeliverable &&
              getValues('Location') && (
                <p className="text-error_500 MuiTypography-body2 mb-16">
                  This item cannot be shipped to your selected delivery
                  location. Please choose a different delivery location.
                </p>
              )}

            {deliveryCheck &&
              deliveryCheck.isDeliverable &&
              getValues('Location') &&
              ((`${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` ==
                type &&
                data?.rentPricing?.[rentIndex]?.priceMode ==
                  RENTAL_SERVICE_TYPE.PER_HOUR &&
                data?.rentPricing?.[rentIndex]?.shiftLength) ||
                `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ==
                  type) &&
              Number(deliveryCheck.distance) > 0 && (
                <p className="MuiTypography-body2 mb-16 text-text mb-24">
                  <i className="info-icon mr-6"></i>
                  The location is{' '}
                  <span className="text-primary_blue_500">
                    {Number(deliveryCheck.distance)
                      .toLocaleString('en-US', {
                        minimumFractionDigits:
                          deliveryCheck.distance % 1 === 0 ? 0 : 2,
                        maximumFractionDigits: 2,
                      })
                      .replace(/,/g, '')}{' '}
                  </span>
                  miles outside of{' '}
                  <span
                    className={
                      deliveryCheck.distance > 0
                        ? 'text-primary_blue_500 underline'
                        : 'text-primary_blue_500'
                    }
                    onClick={() => {
                      if (deliveryCheck.distance > 0)
                        setZoneData({
                          center: {
                            latitude:
                              deliveryCheck?.mileageZone.center.coordinates[1],
                            longitude:
                              deliveryCheck?.mileageZone.center.coordinates[0],
                          },
                          radius: deliveryCheck?.mileageZone?.miles * 1609.34,
                          label: deliveryCheck?.mileageZone.zoneName,
                        });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {deliveryCheck.mileageZone.zoneName}
                  </span>{' '}
                  . Additional charges are applicable.
                </p>
              )}

            {/* <p className="text-gray_500 MuiTypography-body2 mb-16">
              Changing the set location will update the location for all
              selected items.
            </p> */}

            {/* <div className="order-stepper">
              <p className="text-white">Selected Items</p>
              <p className="text-white">1/4</p>
            </div> */}

            <RenderProductInfo
              data={data}
              type={type}
              rentIndex={Number(rentIndex) || 0}
            />

            {type.includes('RENT') &&
              data?.rentPricing?.[rentIndex]?.rentPrice <
                schdulesTypes?.specificRateModel?.rentPrice && (
                <p className="MuiTypography-body4 mb-12 fw400 text-gray_600">
                  The rates are updated by seller.
                </p>
              )}
            {!type.includes('RENT') &&
              data.finalPrice < schdulesTypes?.specificRatePrice && (
                <p className="MuiTypography-body4 mb-12 fw400 text-gray_600">
                  The rates are updated by seller.
                </p>
              )}
            {type == `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ||
            type == `${PRODUCT_TYPES.PRODUCT}-${RENTAL_SERVICE_TYPE.RENT}` ||
            type == `${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` ? (
              <p className="MuiTypography-body1 mb-12 fw600">
                Add Rental Detail
              </p>
            ) : (
              <p className="MuiTypography-body1 mb-12 fw600">
                Add Purchase Detail
              </p>
            )}

            <Grid container spacing={2}>
              <Grid item lg={6} md={6} xs={12}>
                <div className="form-in lg">
                  <FormControl fullWidth>
                    <div className="f-in">
                      <AutocompleteSelect
                        id="Department"
                        name={`Department`}
                        control={control}
                        labelKey="deptName"
                        labelText="Department"
                        placeHolderText="Select Department"
                        options={departmentList.length ? departmentList : []}
                        error={!!errors['Department'] && !!errors['Department']}
                        setValue={setValue}
                        helperText={
                          !!errors['Department']
                            ? (errors['Department']?.message as string)
                            : ''
                        }
                        requiredField
                        disabled={
                          !getValues('Location') || !deliveryCheck.isDeliverable
                        }
                      />
                    </div>
                  </FormControl>
                </div>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <div className="form-in lg">
                  <FormControl fullWidth>
                    <div className="f-in">
                      <AutocompleteSelect
                        id=""
                        name={`Production Schedule`}
                        control={control}
                        labelText="Production Schedule Type"
                        placeHolderText="Select Production Schedule Type"
                        options={schdulesTypes?.productionScheduleType || []}
                        error={
                          !!errors['Production Schedule'] &&
                          !!errors['Production Schedule']
                        }
                        setValue={setValue}
                        helperText={
                          !!errors['Production Schedule']
                            ? (errors['Production Schedule']?.message as string)
                            : ''
                        }
                        requiredField
                        disabled={
                          !getValues('Location') || !deliveryCheck.isDeliverable
                        }
                      />
                    </div>
                  </FormControl>
                </div>
              </Grid>

              <Grid item lg={6} md={6} xs={12}>
                <div className="form-in lg">
                  <FormControl fullWidth>
                    <div className="f-in">
                      <AutocompleteSelect
                        id=""
                        name={`Set Schedule`}
                        control={control}
                        labelText="Set Schedule Type"
                        placeHolderText="Select Set Schedule Type"
                        options={schdulesTypes?.setScheduleType || []}
                        error={
                          !!errors['Set Schedule'] && !!errors['Set Schedule']
                        }
                        setValue={setValue}
                        helperText={
                          !!errors['Set Schedule']
                            ? (errors['Set Schedule']?.message as string)
                            : ''
                        }
                        requiredField
                        disabled={
                          !getValues('Location') || !deliveryCheck.isDeliverable
                        }
                      />
                    </div>
                  </FormControl>
                </div>
              </Grid>
            </Grid>

            {/* show date and time fields for rented product */}

            {type?.includes(`${RENTAL_SERVICE_TYPE.RENT}`) && (
              <Grid container spacing={2} className="mt1">
                <Grid item lg={6} md={6} xs={12}>
                  <div className="form-in lg">
                    <DateInput
                      minDate={dayjs()}
                      requiredField
                      disabled={!getValues('Location')}
                      label={'Start Date'}
                      placeholder="Select Start Date"
                      name="Start Date"
                      formProps={{ control }}
                      error={!!errors['Start Date']}
                      helperText={
                        !!errors['Start Date'] && (
                          <p className="helper-error">
                            {errors['Start Date'].message}
                          </p>
                        )
                      }
                      onChange={(val: any) => {
                        setValue('Start Date', val);
                        setHasChangedAnyValue(true);
                        trigger('Start Date');
                        if (getValues('Start Time')) {
                          trigger('Start Time');
                        }
                        if (getValues('End Date')) {
                          trigger('End Date');
                        }
                        if (getValues('End Time')) {
                          trigger('End Time');
                        }
                        if (
                          val &&
                          type?.includes(RENTAL_SERVICE_TYPE.SERVICE) &&
                          data?.rentPricing?.[rentIndex]?.priceMode !==
                            RENTAL_SERVICE_TYPE?.PER_HOUR
                        ) {
                          setValue('Start Time', val, {
                            shouldValidate: true,
                          });

                          setValue('Coverage', 'autoCoverage', {
                            shouldDirty: true,
                            shouldValidate: true,
                          });

                          return;
                        }

                        if (
                          getValues('Coverage') &&
                          getValues('End Date') &&
                          getValues('Start Date') &&
                          getValues('Start Time')
                        ) {
                          populateEndTime();
                        }
                      }}
                      format="MMM DD, YYYY"
                      formatDate={true}
                    />
                  </div>
                </Grid>

                <Grid item lg={6} md={6} xs={12}>
                  <div className="form-in lg">
                    <TimePicker
                      control={control}
                      id=""
                      minutesStep={TIME_PICKER_MINUTES_STEP}
                      label={'Start Time'}
                      name="Start Time"
                      placeholder={'Select Start Time'}
                      error={
                        !!errors['Start Time'] &&
                        errors['Start Time'].message ==
                          'Start time is mandatory'
                      }
                      requiredField={
                        type ==
                          `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ||
                        (type ==
                          `${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` &&
                          data?.rentPricing?.[rentIndex!]?.priceMode ==
                            RENTAL_SERVICE_TYPE.PER_HOUR &&
                          data?.rentPricing?.[rentIndex!]?.shiftLength)
                      }
                      helperText={
                        !!errors['Start Time']
                          ? (errors['Start Time'].message as string)
                          : ''
                      }
                      onChange={(val: any) => {
                        setValue('Start Time', val);
                        setHasChangedAnyValue(true);
                        trigger('Start Time');
                        if (getValues('End Time')) {
                          populateEndTime();
                          trigger('End Time');
                        }
                        if (
                          getValues('Coverage') == 'autoCoverage' ||
                          (!getValues('Coverage') &&
                            (`${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` ==
                              type ||
                              `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ==
                                type))
                        ) {
                          setValue(
                            'Coverage',
                            'autoCoverage',

                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            },
                          );
                        }
                      }}
                      disabled={
                        !getValues('Location') ||
                        !getValues('Start Date') ||
                        (type?.includes(RENTAL_SERVICE_TYPE.SERVICE) &&
                          data?.rentPricing?.[rentIndex]?.priceMode !=
                            RENTAL_SERVICE_TYPE?.PER_HOUR)
                      }
                    />
                  </div>
                  {watch('Start Date') &&
                    type?.includes(RENTAL_SERVICE_TYPE.SERVICE) &&
                    data?.rentPricing?.[rentIndex]?.priceMode !==
                      RENTAL_SERVICE_TYPE?.PER_HOUR && (
                      <p className="MuiTypography-subtitle1 text-text">
                        The start time is flexible, allowing the service to
                        begin at any time
                      </p>
                    )}
                </Grid>

                {/* {watch('Start Date') && watch('Start Time') && <Grid item lg={12} md={12} xs={12}>
                  <p className="text-text">
                    <i className="info-icon mr-6"></i>
                    Your local Date: {dayjs(
                      watch('Start Time')
                    ).format("MMM DD, YYYY, hh:mm A")} {getTimezoneAbbreviation()}
                  </p>
                </Grid>} */}

                {/* Show shift coverage for product and package rented  */}

                {(`${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` ==
                  type ||
                  `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ==
                    type) &&
                  data?.rentPricing?.[rentIndex]?.shiftLength &&
                  data?.rentPricing?.[rentIndex]?.priceMode ==
                    RENTAL_SERVICE_TYPE?.PER_HOUR && (
                    <>
                      <Grid item lg={6} md={6} xs={12}>
                        <div className="form-in lg">
                          <label className="mb-8 mt-10 d-block">
                            Set Shift Coverage <sup>*</sup>
                          </label>

                          <Controller
                            name="Coverage"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                onChange={e => {
                                  setHasChangedAnyValue(true);
                                  field.onChange(e.target.value);
                                  if (!getValues('End Time')) populateEndTime();
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    className: 'mui-select-drop', // Apply your CSS class here
                                    cursor: 'pointer',
                                  },
                                }}
                                value={field.value ?? ''}
                                displayEmpty
                                fullWidth
                                renderValue={selected => {
                                  if (!selected) {
                                    return (
                                      <span style={{ color: '#aaa' }}>
                                        Set Shift Coverage
                                      </span>
                                    );
                                  }

                                  if (selected === 'autoCoverage') {
                                    return coverageDetails(
                                      getValues('Start Time'),
                                      data.rentPricing?.[rentIndex]
                                        ?.shiftLength,
                                    );
                                  }
                                  return selected;
                                }}
                                disabled={!getValues('Start Time')}
                              >
                                <MenuItem value="autoCoverage">
                                  <p className="MuiTypography-body2 mb-4">
                                    {coverageDetails(
                                      getValues('Start Time'),
                                      data.rentPricing?.[rentIndex]
                                        ?.shiftLength,
                                    )}
                                  </p>
                                  <p className="MuiTypography-subtitle1 text-text">
                                    Shift will automatically be scheduled in
                                    12hr increments between the hrs of 3AM and
                                    3PM.
                                  </p>
                                </MenuItem>

                                <MenuItem value="24 Hrs Shift">
                                  <p className="MuiTypography-body2 mb-4">
                                    24 Hrs Shift
                                  </p>
                                  <p className="MuiTypography-subtitle1 text-text">
                                    Service units will be added for
                                    round-the-clock coverage in 12-hr shifts.
                                  </p>
                                </MenuItem>

                                <MenuItem value="Specific Duration">
                                  <p className="MuiTypography-body2 mb-4">
                                    Specific Duration
                                  </p>
                                  <p className="MuiTypography-subtitle1 text-text">
                                    Service will be provided for specific start
                                    and end time on each day (OT and DT may
                                    apply if the duration exceeds the base
                                    rate.)
                                  </p>
                                </MenuItem>
                              </Select>
                            )}
                          />
                        </div>
                      </Grid>
                      <Grid item lg={6} md={6} xs={12}></Grid>
                    </>
                  )}

                <Grid item lg={6} md={6} xs={12}>
                  <div className="form-in lg">
                    <DateInput
                      minDate={new Date(getValues('Start Date'))}
                      requiredField
                      disabled={
                        !getValues('Location') ||
                        (type ==
                          `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ||
                        (type ==
                          `${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` &&
                          data?.rentPricing?.[rentIndex!]?.priceMode ==
                            RENTAL_SERVICE_TYPE.PER_HOUR &&
                          data?.rentPricing?.[rentIndex!]?.shiftLength)
                          ? !getValues('Start Time')
                          : false)
                      }
                      label={'End Date'}
                      placeholder="End Date"
                      name="End Date"
                      formProps={{ control }}
                      error={!!errors['End Date']}
                      helperText={
                        !!errors['End Date'] && (
                          <p className="helper-error">
                            {errors['End Date'].message}
                          </p>
                        )
                      }
                      onChange={(val: any) => {
                        setValue('End Date', val);
                        trigger('End Date');
                        setHasChangedAnyValue(true);
                        if (
                          val &&
                          type?.includes(RENTAL_SERVICE_TYPE.SERVICE) &&
                          data?.rentPricing?.[rentIndex]?.priceMode !=
                            RENTAL_SERVICE_TYPE?.PER_HOUR
                        ) {
                          const formattedDate = dayjs(val).endOf('day');
                          setValue('End Time', dayjs(val?.endOf('day')), {
                            shouldValidate: true,
                          });
                          return;
                        }
                        if (
                          getValues('Start Time') &&
                          getValues('Coverage') &&
                          getValues('Start Date') &&
                          !getValues('End Time')
                        )
                          populateEndTime();
                        if (getValues('End Time')) trigger('End Time');
                      }}
                      formatDate={true}
                      format="MMM DD, YYYY"
                    />
                  </div>
                </Grid>
                <Grid item lg={6} md={6} xs={12}>
                  <div className="form-in lg">
                    <TimePicker
                      control={control}
                      id=""
                      minutesStep={TIME_PICKER_MINUTES_STEP}
                      label={'End Time'}
                      name="End Time"
                      placeholder={'Select End Time'}
                      error={
                        !!errors['End Time'] &&
                        errors['End Time'].message == 'End time is mandatory'
                      }
                      requiredField={
                        type ==
                          `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ||
                        (type ==
                          `${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` &&
                          data?.rentPricing?.[rentIndex!]?.priceMode ==
                            RENTAL_SERVICE_TYPE.PER_HOUR &&
                          data?.rentPricing?.[rentIndex!]?.shiftLength)
                      }
                      helperText={
                        !!errors['End Time'] &&
                        errors['End Time'].message == 'End time is mandatory'
                          ? errors['End Time'].message
                          : ''
                      }
                      onChange={(val: any) => {
                        setValue('End Time', val);
                        setHasChangedAnyValue(true);
                        trigger('End Time');
                      }}
                      disabled={
                        !getValues('Location') ||
                        !getValues('End Date') ||
                        (type?.includes(RENTAL_SERVICE_TYPE.SERVICE) &&
                          data?.rentPricing?.[rentIndex]?.priceMode !=
                            RENTAL_SERVICE_TYPE?.PER_HOUR)
                      }
                    />
                  </div>
                  {watch('End Date') &&
                    type?.includes(RENTAL_SERVICE_TYPE.SERVICE) &&
                    data?.rentPricing?.[rentIndex]?.priceMode !==
                      RENTAL_SERVICE_TYPE?.PER_HOUR && (
                      <p className="MuiTypography-subtitle1 text-text">
                        The end time is flexible, allowing the service to end at
                        any time.
                      </p>
                    )}
                </Grid>
                {!!errors['End Time'] &&
                errors['End Time'].message != 'End time is mandatory' ? (
                  <Grid item lg={12} md={12} xs={12}>
                    <p className="helper-error">
                      {errors['End Time']?.message}
                    </p>
                  </Grid>
                ) : (
                  ''
                )}
                <Grid item lg={12} md={12} xs={12}></Grid>
              </Grid>
            )}
            {/* {watch('End Date') && watch('End Time') && <Grid item lg={12} md={12} xs={12}>
              <p className="text-text">
                <i className="info-icon mr-6"></i>
                Your local Date: {dayjs(
                  `${watch('End Time')}`
                ).format("MMM DD, YYYY hh:mm A")} {getTimezoneAbbreviation()}
              </p>
            </Grid>} */}
            {/* show last duration only when service rent and package and api will be called only when price mode is hour */}
            {type == `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ||
            type == `${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` ? (
              shifts && !shiftsError ? (
                <p className=" mt-5 text-text mb-24">
                  <i className="info-icon mr-6"></i> Last shift duration will
                  be&nbsp;
                  {shifts} hours
                </p>
              ) : (
                shiftsError && (
                  <p className="mt-5 helper-error mb-24">
                    <i className="info-icon mr-6"></i> {shiftsError}
                  </p>
                )
              )
            ) : null}
          </Grid>

          <Grid item lg={3} md={4} xs={12} className="order-right-side">
            <div className="right-order">
              <div className="imageContainerOrder ratio-1 mt0">
                {
                  <ImageCropperInput
                    setLoad={setLoad}
                    setProgress={setUploadProgress}
                    loading={load}
                    ratio={2 / 1}
                    title="Add Document"
                    type="Document"
                    onlyDocs
                    onAddDocument={handleAddDocument}
                    uploadProgress={uploadProgress}
                    documents={documentFields}
                  />
                }
              </div>
              <div className="mt-15 mb-24 docs-list">
                {documentFields.map((doc: any, index) => (
                  <div
                    className="contact-card bg-white"
                    key={doc.id}
                    onClick={() => {
                      if (!/\.(xls|xlsx)$/i.test(doc.docName)) {
                        setPreviewDoc({ url: doc.url, name: doc.docName });
                      } else {
                        const link = document.createElement('a');
                        link.href = doc.url;
                        link.setAttribute('download', doc.docName); // suggest a filename
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                  >
                    <span className="icon-round">
                      {/\.(jpg|jpeg|png|gif)$/i.test(doc.docName) ? (
                        <img src={doc.url || Images.ImageIcon} alt="Document" />
                      ) : /\.(xls|xlsx)$/i.test(doc.docName) ? (
                        <i className="excel-icon"></i>
                      ) : (
                        <i className="pdf-icon"></i>
                        // <img src={Images.PdfIcon} alt="Document" />
                      )}
                    </span>
                    <div className="d-flex flex-between align-center text">
                      <div className="name">
                        <h3 className="MuiTypography-body2 fw600 mb-6" title="">
                          {doc.docName}
                        </h3>
                      </div>
                      <div className="text-right">
                        <button
                          className="blank-btn icon-round grey cursor"
                          type="button"
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeleteOpen(index + 1);
                          }}
                        >
                          <i className="delete-icon grey"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <FormHelperText className="Mui-error">
                  {errors.Attachments && errors.Attachments.message}
                </FormHelperText>
              </div>
              {type?.includes(`${RENTAL_SERVICE_TYPE.RENT}`) && (
                <div className="form-in lg mb-32">
                  <label className="MuiTypography-subtitle1 mb-6 d-flex">
                    Notes
                  </label>

                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Write your notes"
                    value={watch('Notes')}
                    onChange={e => {
                      const val = e.target.value;
                      if (val.length <= 300) {
                        setValue('Notes', val);
                      } else {
                        setValue('Notes', val.slice(0, 300));
                      }
                    }}
                    inputProps={{ maxLength: 300 }}
                    // other props
                  />

                  <FormHelperText className="Mui-error">
                    {errors.Notes && errors.Notes.message}
                  </FormHelperText>
                </div>
              )}

              <div className="btn-grp">
                {(`${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` ==
                  type ||
                  `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` ==
                    type) && (
                  <div>
                    <CustomButton
                      id=""
                      text="Preview Shift"
                      showIcon={false}
                      // onClick={handleClose}
                      variant="outlined"
                      size="medium"
                      className="lg-height text-unset"
                      type="submit"
                      onClick={handleSubmit(data => onSubmit(data, 'preview'))}
                      disabled={
                        !isValid ||
                        !deliveryCheck.isDeliverable ||
                        load ||
                        apiLoading ||
                        shiftsError
                          ? true
                          : false ||
                            data?.branch?.[0]?.publishStatus == 'UNPUBLISHED' ||
                            data?.publishStatus !== 'PUBLISHED'
                      }
                      // onClick={() => navigate('/preview-shift')}
                    />
                  </div>
                )}

                <div>
                  <CustomButton
                    id=""
                    text={
                      cartDetails.cart?.length > 0
                        ? 'Add to Order'
                        : 'Start an Order'
                    }
                    showIcon={false}
                    variant="contained"
                    type="button"
                    size="medium"
                    className="lg-height"
                    // onClick={handleSubmit(onSubmit)}
                    onClick={event => {
                      if (
                        cartDetails?.cart[0]?.groupedProducts[0] &&
                        cartDetails?.cart[0]?.groupedProducts[0]
                          ?.productionId !== getValues('Production')?._id
                      ) {
                        event.preventDefault();
                        setWarningOpen(true);
                      } else {
                        handleSubmit(data => onSubmit(data, 'start'))();
                      }
                    }}
                    disabled={
                      !isValid ||
                      !deliveryCheck.isDeliverable ||
                      load ||
                      apiLoading ||
                      shiftsError
                        ? true
                        : false ||
                          data?.branch?.[0]?.publishStatus == 'UNPUBLISHED' ||
                          data?.publishStatus !== 'PUBLISHED'
                    }
                  />
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </form>
      <SelectAddressDialog
        handleClose={() => setOpenSelectAddress(false)}
        open={openSelectAddress}
        handleLocation={handleLocation}
        getValues={getValues}
      />
      <WarningDialog
        open={warningOpen}
        title="Replace Cart item?"
        description={
          <>
            Your cart contains products for{' '}
            <b style={{ fontWeight: 'bold' }}>
              '
              {cartDetails &&
                cartDetails.cart &&
                cartDetails?.cart[0]?.groupedProducts[0]?.productionName}
              '
            </b>
            . Do you want to discard the selection and place an order for{' '}
            <b style={{ fontWeight: 'bold' }}>
              '{getValues('Production')?.name ?? ''}'
            </b>
            ?
          </>
        }
        type="warning"
        handleClose={() => setWarningOpen(false)}
        onClick={() => {
          handleSubmit(data => onSubmit(data, 'start'))();
          setWarningOpen(false);
        }}
        cancelBtnText="No"
        confirmBtnText="Yes"
      />
      <WarningDialog
        open={deleteOpen !== 0}
        title="Delete this attachment?"
        description={'Are you sure you want to delete this attachment?'}
        type="delete"
        handleClose={() => setDeleteOpen(0)}
        onClick={() => {
          removeDocuments(deleteOpen - 1);
          setDeleteOpen(0);
        }}
        cancelBtnText="Cancel"
        confirmBtnText="Delete"
      />

      <PreviewDialog previewDoc={previewDoc} handleClose={handleClose} />
      <MapModal
        open={zoneData?.radius != 0}
        handleClose={() =>
          setZoneData({
            center: { latitude: 0, longitude: 0 },
            radius: 0,
            label: '',
          })
        }
        zoneData={zoneData}
        userLocation={getValues('Location')}
      ></MapModal>
    </>
  );
};

export default OrderForm;
